import {ElementCursor, ElementProperties, ElementView} from '@app/modules/svg/element/ElementView';
import {MeshView, StyledEdge} from '@app/modules/svg/element/shape/pointed/MeshView';
import {Container} from '@app/modules/svg/Container';
import {Rect} from '@app/modules/svg/model/Rect';
import {MoveDrawable} from '@app/modules/svg/service/tool/draw/type/MoveDrawable';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {LineTo} from '@app/modules/svg/model/path/line/LineTo';
import {Style} from '@app/modules/svg/service/style/Style';
import {PathCommand} from '@app/modules/svg/model/path/PathCommand';
import {Point} from '@app/modules/svg/model/Point';
import {Polygon} from '@app/modules/svg/service/math/Polygon';
import {Line} from '@app/modules/svg/model/Line';
import {linesEqual} from '@app/modules/svg/service/math/Line';

export class PrismCursor extends ElementCursor {}

export interface PrismProperties extends ElementProperties {
  baseVertexCount?: number;
}

export class PrismView extends MeshView implements MoveDrawable {
  protected _type: ElementType = ElementType.PRISM;

  public constructor(container: Container,
                     properties: PrismProperties = {},
                     rect: Rect = {x: 0, y: 0, width: 1, height: 1},
                     ownerId?: string, index?: number) {

    const cube = PrismView.rectToPrismMesh(rect, properties.baseVertexCount);
    super(container, properties, cube.points, cube.edges, ownerId, index);
    this.svgElement.id = this.id;

    this._rect = ElementView.calculateRect(this.points);
    this.__fixRect__();

    this.setProperties(properties);
  }

  public __drawSize__(rect: Rect): void {
    const cube = PrismView.rectToPrismMesh(rect, (this.properties as PrismProperties).baseVertexCount);
    this._points = cube.points;
    this._styledEdges = cube.edges;

    this._rect = ElementView.calculateRect(this.points);

    this.__updateView__();
  }

  private static rectToPrismMesh(rect: Rect, baseVertexCount: number): { points: Point[]; edges: StyledEdge[] } {
    const dashLineStyle: Style = new Style();
    dashLineStyle.strokeDashArray = '4 4';

    const ropBaseCenter: Point = {
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height * (1/6),
    };
    const topBaseAsPolygon: Point[] = Polygon.getEllipticPoints({
      cx: ropBaseCenter.x,
      cy: ropBaseCenter.y,
      rx: rect.width / 2,
      ry: rect.height * (1/6)
    }, baseVertexCount);

    const bottomBaseCenter: Point = {
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height * (5/6),
    };
    const bottomBaseAsPolygon: Point[] = Polygon.getEllipticPoints({
      cx: bottomBaseCenter.x,
      cy: bottomBaseCenter.y,
      rx: rect.width / 2,
      ry: rect.height * (1/6)
    }, baseVertexCount);

    const points: Point[] = [];

    for (const point of topBaseAsPolygon) {
      points.push(point);
    }
    for (const point of bottomBaseAsPolygon) {
      points.push(point);
    }

    const firstPoint: Point = bottomBaseAsPolygon[0];
    const sweepLine: Line = bottomBaseAsPolygon
        .reduce((line: Line, point: Point): Line => {
          if (point.x <= line.p0.x) {
            line.p0 = point;
          }

          if (point.x >= line.p1.x) {
            line.p1 = point;
          }

          return line;
        }, { p0: firstPoint, p1: firstPoint });

    const edges: StyledEdge[] = [];

    for (let pointIndex = 0; pointIndex < topBaseAsPolygon.length; pointIndex++) {
      const nextPointIndex: number = pointIndex + 1 < topBaseAsPolygon.length ? pointIndex + 1 : 0;

      /* top base */
      const currentTopEdge: StyledEdge = {point1: pointIndex, point2: nextPointIndex, command: new LineTo({x: 0, y: 0})};
      edges.push(currentTopEdge);

      /* bottom base */
      const currentPoint: Point = bottomBaseAsPolygon[pointIndex];
      const nextPoint: Point = bottomBaseAsPolygon[nextPointIndex];

      const isCurrentPointVisible: boolean = PrismView.isPointVisible(currentPoint, sweepLine);
      const isNextPointVisible: boolean = PrismView.isPointVisible(nextPoint, sweepLine);

      const currentBottomEdge: StyledEdge = {point1: pointIndex + baseVertexCount , point2: nextPointIndex + baseVertexCount, command: new LineTo({x: 0, y: 0})};

      let isEdgeVisible: boolean;
      if (baseVertexCount === 3) {
        isEdgeVisible = !linesEqual(sweepLine, {p0: currentPoint, p1: nextPoint});
      } else {
        isEdgeVisible = isCurrentPointVisible && isNextPointVisible;
      }

      if (!isEdgeVisible) {
        currentBottomEdge.style = dashLineStyle;
      }

      edges.push(currentBottomEdge);

      const verticalEdge: StyledEdge = {point1: pointIndex, point2: pointIndex + baseVertexCount, command: new LineTo({x: 0, y: 0})};
      if (!isCurrentPointVisible) {
        verticalEdge.style = dashLineStyle;
      }

      edges.push(verticalEdge);
    }

    return {
      points,
      edges
    };
  }

  private static isPointVisible(point: Point, sweepLine: Line): boolean {
    return point.y >= sweepLine.p0.y || point.y >= sweepLine.p1.y;
  };
}
